import type {
  ChannelsFilter,
  ChannelsResponse,
  ChannelThresholdsFilter,
  CollectionResourcesFilter,
  CollectionResourcesResponse,
  CollectionsFilter,
  CollectionsResponse,
  CommandExecutionResponse,
  DeviceResponse,
  UpdateDevice,
} from "../schemas.ts";
import type {
  ChannelIncidentsFilterFixed,
  ChannelIncidentsResponse,
  ChannelThresholdsResponse,
  CommandDeviceFixed,
  CreateAutomationRespone,
  DataPointsFilterFixed,
  DataPointsResponse,
  DevicesWithChannelsResponse,
  FixedAutomationsResponse,
  ChannelThresholdInput,
} from "./laboperator.types";
import { APiError } from "./common.ts";

type Path = `/${string}`;

type LOErrorBody = {
  errors: [{
  "status": string;
  "code": string;
  "detail": string;
  "title": string;
  }]
}

export const BASE_PATH = import.meta.env.VITE_BASE_PATH;
export const LABOPERATOR_ENDPOINT = import.meta.env.VITE_LABOPERATOR_ENDPOINT;

function validateAndResolve (res: Response): Promise<any> {
  if (res.status >= 200 && res.status < 300) return res.json();
  // extract error from payload
  return Promise
    .resolve(res.json())
    .then((json: object) => {
      throw new APiError(res.statusText, res.status, json as LOErrorBody);
    })
}

function handleFetchError(_reason: any): Promise<any> {
  return Promise.reject(new APiError(`API call failed`, 503))
}

const fetchLabOperator = (path: Path, token: string, body?: Object) => {
  if (path.includes('undefined')) {
    console.error('fetchLabOperator called with undefined in path:', path, new Error().stack);
    return Promise.reject(new Error('Invalid path with undefined'));
  }
  return fetch(`${BASE_PATH}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
    .catch(handleFetchError)
    .then(validateAndResolve);
};

const putLabOperator = (path: Path, token: string, body: Object) => {
  if (path.includes('undefined')) {
    console.error('putLabOperator called with undefined in path:', path, new Error().stack);
    return Promise.reject(new Error('Invalid path with undefined'));
  }
  const authHeaders: HeadersInit = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  return fetch(`${BASE_PATH}${path}`, {
    method: "PUT",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  })
    .catch(handleFetchError)
    .then(validateAndResolve);
};

const deleteLabOperator = (path: Path, token: string, body?: Object) => {
  const authHeaders: HeadersInit = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  return fetch(`${BASE_PATH}${path}`, {
    method: "DELETE",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  })
    .catch(handleFetchError)
    .then(validateAndResolve);
};
const patchLabOperator = (path: Path, token: string, body: Object) => {
  if (path.includes('undefined')) {
    console.error('patchLabOperator called with undefined in path:', path, new Error().stack);
    return Promise.reject(new Error('Invalid path with undefined'));
  }
  const authHeaders: HeadersInit = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  return fetch(`${BASE_PATH}${path}`, {
    method: "PATCH",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  })
    .catch(handleFetchError)
    .then(validateAndResolve);
};

const postLabOperator = (path: Path, token: string, body?: FormData) => {
  const authHeaders: HeadersInit = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  return fetch(`${BASE_PATH}${path}`, {
    method: "POST",
    headers: {
      ...authHeaders,
    },
    body: body,
  })
    .catch(handleFetchError)
    .then(validateAndResolve);
};

export const receiveToken = () =>
  fetch(`${LABOPERATOR_ENDPOINT}/api/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "client_credentials",
      scope: "read write delete",
      client_id: import.meta.env.VITE_CLIENT_ID,
      client_secret: import.meta.env.VITE_CLIENT_SECRET,
    }),
  }).then((res) => res.json());

export const receiveAllDevices = (
  token: string,
  deviceIds?: string[]
): Promise<DevicesWithChannelsResponse> => {
  const params = new URLSearchParams();
  params.set("filter[devices][hidden_at][nil]", "true");
  params.set("include", "channels");
  params.set("fields[channels]", "name,unit,interval,status");
  params.set("filter[channels][disabled]", "false");
  params.set("page[channels][size]", "1000");
  if(deviceIds){
    params.set("filter[devices][id]", deviceIds.join(","));
  }
  return fetchLabOperator(`/devices?${params.toString()}`, token);
};

export const queryChannels = async (
  token: string,
  filter: ChannelsFilter
): Promise<ChannelsResponse> => {
  const params = new URLSearchParams();
  let deviceId: string | undefined;
  let canId: string | undefined;
  if (filter.device?.id && filter.device.id.length > 0) {
    // deconstruct device id for fumehoods
    [deviceId, canId] = filter.device.id[0].split("-");
    params.set("filter[channels][device_id]", deviceId);
  }
  params.set("page[channels][size]", "1000");

  const result: ChannelsResponse = await fetchLabOperator(
    `/channels?${params.toString()}`,
    token
  );
  if (!canId) return result;

  return {
    ...result,
    // filter retrieved channels for canID
    data: result.data?.filter((c) =>
      c.attributes?.name?.startsWith(`can_id_${canId}_`)
    ),
  };
};

export const receiveDataPoints = (
  token: string,
  filter: DataPointsFilterFixed,
  limit?: number,
  groupTimeRange?: string,
  sampleSize?: number
): Promise<DataPointsResponse> => {
  const params = new URLSearchParams();
  if (!filter.data_points?.channel_id)
    throw new Error("filter to channel-id is required");

  params.set(
    "filter[data_points][channel_id]",
    filter.data_points.channel_id
      .filter((c) => c !== null && c !== "")
      .join(",")
  );

  // filter on time range
  if (filter.data_points.timestamp?.le) {
    params.append(
      `filter[data_points][timestamp][le]`,
      filter.data_points.timestamp.le.toISOString()
    );
  }
  if (filter.data_points.timestamp?.ge) {
    params.append(
      `filter[data_points][timestamp][ge]`,
      filter.data_points.timestamp.ge.toISOString()
    );
  }

  // filter on value
  if (filter.data_points.value?.le) {
    params.append(
      `filter[data_points][value][le]`,
      filter.data_points.value.le.toString(10)
    );
  }
  if (filter.data_points.value?.ge) {
    params.append(
      `filter[data_points][value][ge]`,
      filter.data_points.value.ge.toString(10)
    );
  }

  if (limit) {
    params.set("limit", limit.toString());
  }

  if (sampleSize && !groupTimeRange && !limit) {
    params.set("sampled", "true");
    params.set("sample_size", sampleSize.toString());
  }

  if (groupTimeRange && !sampleSize && !limit) {
    params.set("grouped", "true");
    params.set("aggregate", "avg");
    params.set("bucket_size", groupTimeRange);
  }

  return fetchLabOperator(`/data_points?&${params.toString()}`, token);
};

export const queryChannelIncidents = async (
  token: string,
  filter: ChannelIncidentsFilterFixed = {},
  statusChannelIds?: number[]
): Promise<ChannelIncidentsResponse> => {
  const params = new URLSearchParams();
  params.set("page[channel_incidents][size]", filter.page ? "50" : "1000");
  if(filter.page) {
    params.set("page[channel_incidents][number]", filter.page);
  }
  params.set("include", "channel,channel_threshold");
  if (filter.channel?.id && filter.channel?.id.length > 0) {
    params.set("filter[channel][channel_id]", filter.channel?.id.join(","));
  }

  if (filter.channel_incidents?.["channels.device_id"] && filter.channel_incidents?.["channels.device_id"].length > 0) {
    params.set("filter[channel_incidents][channels.device_id]", filter.channel_incidents?.["channels.device_id"].join(","));
  }

  if (statusChannelIds) {
    params.set(
      "filter[channel_incidents][channel_id]",
      statusChannelIds.join(",")
    );
  }
  if (filter.channel_threshold?.id && filter.channel_threshold?.id.length > 0) {
    params.set(
      "filter[channel_thresholds][id]",
      filter.channel_threshold?.id.join(",")
    );
  }
  if (filter.sort?.channel_incidents) {
    params.set("sort[channel_incidents]", filter.sort?.channel_incidents);
  }

  if (filter.channel_incidents?.closed === true) {
    params.set("filter[channel_incidents][closed_at][nil]","false");
  } else if (filter.channel_incidents?.closed === false) {
    params.set("filter[channel_incidents][closed_at][nil]","true");
  }
  return fetchLabOperator(`/channel_incidents?${params.toString()}`, token);
};

export const sendCommand = (
  token: string,
  deviceId: string,
  command: CommandDeviceFixed
): Promise<CommandExecutionResponse> => {
  if (!deviceId) {
    console.error('sendCommand called with undefined deviceId', new Error().stack);
    return Promise.reject(new Error('deviceId is required'));
  }
  return putLabOperator(`/devices/${deviceId}/command`, token, command);
};

export const getCommand = (
  token: string,
  commandId: number
): Promise<CommandExecutionResponse> =>
  fetchLabOperator(`/command_executions/${commandId}`, token);

export const receiveAllAutomations = (
  token: string
): Promise<FixedAutomationsResponse> => {
  const params = new URLSearchParams();
  params.set("template", "true");
  params.set("page[automations][size]", "1000");

  return fetchLabOperator(`/automations?${params.toString()}`, token);
}

export const deleteAutomation = async (token: string, automationId: string) => deleteLabOperator(`/automations/${automationId}`, token);

export const updateAutomation = async (
  token: string,
  automationId: string,
  name: string,
  active: boolean
) => {
  const data = {
    data: {
      type: "automation",
      attributes: {
        active: active,
        name: name,
      },
    },
  };

  return patchLabOperator(`/automations/${automationId}`, token, data);
};

export const createAutomation = (
  token: string,
  automation: {bodyData: Object, fileContent: Object}
): Promise<CreateAutomationRespone> =>{
  const { bodyData, fileContent } = automation;
  const data = new FormData();
  data.append("data", JSON.stringify(bodyData));
  const bytes = new TextEncoder().encode(JSON.stringify(fileContent))
  const blob = new Blob([bytes], {type: "application/json;charset=utf-8"})
  data.append("file", blob, "automation.json" );
  return postLabOperator("/automations", token, data);
}

export const updateDevice = (
  token: string,
  deviceId: string,
  data: Record<string, unknown>
): Promise<CommandExecutionResponse> => {
  if (!deviceId) {
    console.error('updateDevice called with undefined deviceId', new Error().stack);
    return Promise.reject(new Error('deviceId is required'));
  }
  return patchLabOperator(`/devices/${deviceId}`, token, data);
};

export const receiveDevice = ( token: string, deviceId: string, fields?: string[]) => {
  if (!deviceId) {
    console.error('receiveDevice called with undefined deviceId', new Error().stack);
    return Promise.reject(new Error('deviceId is required'));
  }
  const params = new URLSearchParams();
  if(fields){
    params.set("fields[devices]", fields.join(","));
  }
  params.set("filter[devices][hidden_at][nil]", "true");
  params.set("include", "channels");
  params.set("fields[channels]", "name,unit,interval,status");
  params.set("filter[channels][disabled]", "false");
  params.set("page[channels][size]", "1000");
  return fetchLabOperator(`/devices/${deviceId}?${params.toString()}`, token);
}

export const getCollections = (token: string,
  filter: CollectionsFilter = {},) => {
  const params = new URLSearchParams();
  params.set("filter[collections][hidden_at][nil]", "true");
  setFilterParams(filter, params, "filter")
  return fetchLabOperator(`/collections?${params.toString()}`, token) as Promise<CollectionsResponse>;
}

export const getCollectionResources = (token: string,
  filter: CollectionResourcesFilter = {},) => {
  const params = new URLSearchParams();
  params.set("filter[collection_resources][search_docs][hidden_at][nil]", "true");
  setFilterParams(filter, params, "filter")
  return fetchLabOperator(`/collection_resources?${params.toString()}`, token) as Promise<CollectionResourcesResponse>;
}

function setFilterParams(filter: { [x: string]: any; }, params: { set: (arg0: string, arg1: any) => void; }, prefix = 'filter') {
  Object.keys(filter).forEach(key => {
    if (typeof filter[key] === 'object' && !Array.isArray(filter[key]) && filter[key] !== null) {
      setFilterParams(filter[key], params, `${prefix}[${key}]`);
    } else {
      let value = filter[key];
      if (Array.isArray(value)) {
        value = value.join(",");
      }
      params.set(`${prefix}[${key}]`, value);
    }
  });
}

export const receiveChannelThresholds = (
  token: string,
  filter: ChannelThresholdsFilter
): Promise<ChannelThresholdsResponse> => {
  const params = new URLSearchParams();
  params.set("page[channel_thresholds][size]", "100");
  if (
    filter.channel_thresholds?.channel_id &&
    filter.channel_thresholds?.channel_id.length > 0
  ) {
    params.set(
      "filter[channel_thresholds][channel_id]",
      filter.channel_thresholds?.channel_id.join(",")
    );
  }
  if (
    filter.channel_thresholds?.id &&
    filter.channel_thresholds?.id.length > 0
  ) {
    params.set(
      "filter[channel_thresholds][id]",
      filter.channel_thresholds?.id.join(",")
    );
  }
  return fetchLabOperator(`/channel_thresholds?${params.toString()}`, token);
};

type ThresholdConfig = {
  highAlert?: number;
  highWarning?: number;
  lowWarning?: number;
  lowAlert?: number;
  delay?: number;
};

export const updateChannelThresholdsSimple = async (
  token: string,
  deviceId: string,
  channelId: string,
  config: ThresholdConfig
): Promise<DeviceResponse> => {
  const thresholds = [];

  if (config.highAlert !== undefined) {
    thresholds.push({
      level: "critical" as const,
      value: config.highAlert.toString(),
      comparator: "above" as const,
      duration: config.delay ?? 0,
      condition: "for_at_least" as const
    });
  }

  if (config.highWarning !== undefined) {
    thresholds.push({
      level: "warning" as const,
      value: config.highWarning.toString(),
      comparator: "above" as const,
      duration: config.delay ?? 0,
      condition: "for_at_least" as const
    });
  }

  if (config.lowWarning !== undefined) {
    thresholds.push({
      level: "warning" as const,
      value: config.lowWarning.toString(),
      comparator: "below" as const,
      duration: config.delay ?? 0,
      condition: "for_at_least" as const
    });
  }

  if (config.lowAlert !== undefined) {
    thresholds.push({
      level: "critical" as const,
      value: config.lowAlert.toString(),
      comparator: "below" as const,
      duration: config.delay ?? 0,
      condition: "for_at_least" as const
    });
  }

  return updateChannelThresholds(token, deviceId, {
    channelId,
    thresholds
  });
};

export function updateChannelThresholds(
  token: string,
  deviceId: string,
  input: ChannelThresholdInput
): Promise<DeviceResponse> {
  return receiveChannelThresholds(token, {
    channel_thresholds: { channel_id: [input.channelId] }
  }).then(async existingThresholds => {
    if (existingThresholds.data) {
      const thresholdsToDelete = existingThresholds.data
        .filter(existing =>
          // Delete if value changed for same level/comparator
          input.thresholds.some(newThreshold =>
            existing.attributes.level === newThreshold.level &&
            existing.attributes.comparator === newThreshold.comparator &&
            existing.attributes.value !== newThreshold.value
          ) ||
          !input.thresholds.some(newThreshold =>
            existing.attributes.level === newThreshold.level &&
            existing.attributes.comparator === newThreshold.comparator
          )
        );

      await Promise.all(
        thresholdsToDelete.map(threshold =>
          deleteChannelThreshold(token, Number(threshold.id))
        )
      );
    }

    const thresholds_attributes = input.thresholds
      .filter(newThreshold => {
        // Skip if an identical threshold already exists
        return !existingThresholds.data?.some(existing =>
          existing.attributes.level === newThreshold.level &&
          existing.attributes.comparator === newThreshold.comparator &&
          existing.attributes.value === newThreshold.value &&
          existing.attributes.duration === newThreshold.duration
        );
      })
      .map(newThreshold => ({
        level: newThreshold.level,
        value: newThreshold.value,
        comparator: newThreshold.comparator,
        duration: newThreshold.duration,
        condition: "for_at_least",
        condition_times: 1,
        metric: "channel_value",
        reference_channel_id: null
      }));

    if (thresholds_attributes.length === 0) {
      console.log("No new thresholds to create");
      return { data: null };
    }

    const channelAttUpdates = [{
      id: Number.parseInt(input.channelId),
      thresholds_attributes
    }];

    const body: UpdateDevice = {
      data: {
        type: "device",
        attributes: {
          channels_attributes: channelAttUpdates,
        },
        relationships: {},
      },
    };

    return patchLabOperator(`/devices/${deviceId}`, token, body);
  });
}

export const deleteChannelThreshold = (
  token: string,
  thresholdId: number
): Promise<Response> => {
  return deleteLabOperator(`/channel_thresholds/${thresholdId}`, token);
};

export const deleteChannelThresholds = async (
  token: string,
  channelId: string
): Promise<void> => {
  const existingThresholds = await receiveChannelThresholds(token, {
    channel_thresholds: { channel_id: [channelId] }
  });

  if (existingThresholds.data) {
    await Promise.all(
      existingThresholds.data.map(threshold =>
        deleteChannelThreshold(token, Number(threshold.id))
      )
    );
  }
};

/**
 * Updates a collection with new data
 * @param token Access token for authentication
 * @param collectionId The ID of the collection to update
 * @param data The updated collection data
 * @returns Promise with the updated collection
 */
export const updateCollection = (
  token: string,
  collectionId: string,
  data: {
    data: {
      type: string;
      attributes: {
        name?: string;
        custom_attributes?: Array<{
          name: string;
          type: string;
          options: Record<string, any>;
          value?: any;
        }>;
      };
      relationships?: Record<string, any>;
    }
  }
): Promise<any> => {
  return patchLabOperator(`/collections/${collectionId}`, token, data);
};
